/**
 * Alshallal Pastry Company - My Letters Page (Employee Self-Service)
 * Phase 25: Letters & Templates System
 * Phase 25.1: Enhanced with Document Preview component
 * 
 * Features:
 * - View own letter requests and issued documents
 * - Submit new letter requests
 * - Preview and download issued letters
 * - IDOR Protected: Employee can only see their own letters
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { toast } from 'sonner';
import {
  FileText,
  Plus,
  Download,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Eye,
  Loader2,
  Send,
  FileCheck
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import DocumentPreview from '../../components/documents/DocumentPreview';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  pending: { label: 'Pending', labelAr: 'قيد الانتظار', icon: Clock, color: 'bg-yellow-100 text-yellow-800' },
  under_review: { label: 'Under Review', labelAr: 'قيد المراجعة', icon: Eye, color: 'bg-blue-100 text-blue-800' },
  approved: { label: 'Approved', labelAr: 'معتمد', icon: CheckCircle, color: 'bg-green-100 text-green-800' },
  rejected: { label: 'Rejected', labelAr: 'مرفوض', icon: XCircle, color: 'bg-red-100 text-red-800' },
  issued: { label: 'Issued', labelAr: 'صادر', icon: FileCheck, color: 'bg-emerald-100 text-emerald-800' },
  cancelled: { label: 'Cancelled', labelAr: 'ملغي', icon: AlertCircle, color: 'bg-gray-100 text-gray-800' },
};

const LANGUAGE_OPTIONS = [
  { value: 'ar', label: 'Arabic', labelAr: 'العربية' },
  { value: 'en', label: 'English', labelAr: 'الإنجليزية' },
  { value: 'ar_en', label: 'Bilingual (AR/EN)', labelAr: 'ثنائي اللغة' },
];

export default function MyLettersPage() {
  const { token } = useAuth();
  const { language } = useLanguage();
  const isRTL = language === 'ar';

  const [requests, setRequests] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [letterTypes, setLetterTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ pending: 0, issued: 0 });
  
  // New Request Dialog
  const [showNewRequest, setShowNewRequest] = useState(false);
  const [newRequest, setNewRequest] = useState({
    letter_type_code: '',
    language: 'ar_en',
    employee_notes: ''
  });
  const [submitting, setSubmitting] = useState(false);
  
  // Request Details Dialog
  const [selectedRequest, setSelectedRequest] = useState(null);

  // Document Preview Dialog (Phase 25.1)
  const [previewDialog, setPreviewDialog] = useState({ open: false, document: null });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [requestsRes, documentsRes, typesRes, statsRes] = await Promise.all([
        fetch(`${API}/api/letters/my/requests?page_size=50`, { headers }),
        fetch(`${API}/api/letters/my/documents?page_size=50`, { headers }),
        fetch(`${API}/api/letters/types`, { headers }),
        fetch(`${API}/api/letters/stats`, { headers }),
      ]);

      if (requestsRes.ok) {
        const data = await requestsRes.json();
        setRequests(data.requests || []);
      }
      
      if (documentsRes.ok) {
        const data = await documentsRes.json();
        setDocuments(data.documents || []);
      }
      
      if (typesRes.ok) {
        const data = await typesRes.json();
        setLetterTypes(data.letter_types || []);
      }
      
      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error(isRTL ? 'خطأ في تحميل البيانات' : 'Error loading data');
    } finally {
      setLoading(false);
    }
  }, [token, isRTL]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmitRequest = async () => {
    if (!newRequest.letter_type_code) {
      toast.error(isRTL ? 'يرجى اختيار نوع الخطاب' : 'Please select a letter type');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/letters/my/requests`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newRequest),
      });

      if (res.ok) {
        toast.success(isRTL ? 'تم تقديم الطلب بنجاح' : 'Request submitted successfully');
        setShowNewRequest(false);
        setNewRequest({ letter_type_code: '', language: 'ar_en', employee_notes: '' });
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail?.error || 'Failed to submit request');
      }
    } catch (error) {
      console.error('Error submitting request:', error);
      toast.error(isRTL ? 'خطأ في تقديم الطلب' : 'Error submitting request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownload = async (documentId, referenceNo) => {
    try {
      const res = await fetch(`${API}/api/letters/my/documents/${documentId}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${referenceNo}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success(isRTL ? 'تم تحميل الخطاب' : 'Letter downloaded');
        fetchData(); // Refresh to update download count
      } else {
        const error = await res.json();
        toast.error(error.detail?.error || 'Download failed');
      }
    } catch (error) {
      console.error('Download error:', error);
      toast.error(isRTL ? 'خطأ في التحميل' : 'Download error');
    }
  };

  const getLetterTypeName = (code) => {
    const type = letterTypes.find(t => t.code === code);
    if (!type) return code;
    return isRTL ? type.name_ar : type.name_en;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString(isRTL ? 'ar-SA' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className={`space-y-6 p-6 ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isRTL ? 'خطاباتي' : 'My Letters'}
          </h1>
          <p className="text-gray-500 mt-1">
            {isRTL ? 'طلب وتحميل الخطابات الرسمية' : 'Request and download official letters'}
          </p>
        </div>
        <Button onClick={() => setShowNewRequest(true)} data-testid="new-letter-request-btn">
          <Plus className="w-4 h-4 mr-2" />
          {isRTL ? 'طلب خطاب جديد' : 'Request New Letter'}
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-yellow-100 rounded-lg">
                <Clock className="w-6 h-6 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">{isRTL ? 'طلبات معلقة' : 'Pending Requests'}</p>
                <p className="text-2xl font-bold">{stats.pending || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-emerald-100 rounded-lg">
                <FileCheck className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">{isRTL ? 'خطابات صادرة' : 'Issued Letters'}</p>
                <p className="text-2xl font-bold">{stats.issued || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="requests" className="space-y-4">
        <TabsList>
          <TabsTrigger value="requests" data-testid="requests-tab">
            <FileText className="w-4 h-4 mr-2" />
            {isRTL ? 'الطلبات' : 'Requests'}
          </TabsTrigger>
          <TabsTrigger value="documents" data-testid="documents-tab">
            <FileCheck className="w-4 h-4 mr-2" />
            {isRTL ? 'الخطابات الصادرة' : 'Issued Letters'}
          </TabsTrigger>
        </TabsList>

        {/* Requests Tab */}
        <TabsContent value="requests">
          <Card>
            <CardHeader>
              <CardTitle>{isRTL ? 'طلباتي' : 'My Requests'}</CardTitle>
              <CardDescription>
                {isRTL ? 'جميع طلبات الخطابات المقدمة' : 'All submitted letter requests'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {requests.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>{isRTL ? 'لا توجد طلبات' : 'No requests found'}</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {requests.map((request) => {
                    const statusConfig = STATUS_CONFIG[request.status] || STATUS_CONFIG.pending;
                    const StatusIcon = statusConfig.icon;
                    
                    return (
                      <div
                        key={request.id}
                        className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                        data-testid={`request-item-${request.id}`}
                      >
                        <div className="flex items-center gap-4">
                          <div className="p-2 bg-gray-100 rounded-lg">
                            <FileText className="w-5 h-5 text-gray-600" />
                          </div>
                          <div>
                            <p className="font-medium">
                              {isRTL ? request.letter_type_name_ar : request.letter_type_name_en}
                            </p>
                            <p className="text-sm text-gray-500">
                              {formatDate(request.created_at)}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <Badge className={statusConfig.color}>
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {isRTL ? statusConfig.labelAr : statusConfig.label}
                          </Badge>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setSelectedRequest(request)}
                            data-testid={`view-request-${request.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent value="documents">
          <Card>
            <CardHeader>
              <CardTitle>{isRTL ? 'الخطابات الصادرة' : 'Issued Letters'}</CardTitle>
              <CardDescription>
                {isRTL ? 'الخطابات الجاهزة للتحميل' : 'Letters ready for download'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {documents.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <FileCheck className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>{isRTL ? 'لا توجد خطابات صادرة' : 'No issued letters'}</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {documents.map((doc) => (
                    <div
                      key={doc.id}
                      className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                      data-testid={`document-item-${doc.id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="p-2 bg-emerald-100 rounded-lg">
                          <FileCheck className="w-5 h-5 text-emerald-600" />
                        </div>
                        <div>
                          <p className="font-medium">
                            {isRTL ? doc.letter_type_name_ar : doc.letter_type_name_en}
                          </p>
                          <p className="text-sm text-gray-500">
                            {doc.reference_no} • {formatDate(doc.issued_at)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-sm text-gray-500">
                          {doc.download_count} {isRTL ? 'تحميل' : 'downloads'}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setPreviewDialog({ 
                            open: true, 
                            document: { 
                              id: doc.id, 
                              file_name: `${doc.reference_no}.pdf`,
                              reference_no: doc.reference_no
                            }
                          })}
                          title={isRTL ? 'معاينة' : 'Preview'}
                          data-testid={`preview-document-${doc.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDownload(doc.id, doc.reference_no)}
                          data-testid={`download-document-${doc.id}`}
                        >
                          <Download className="w-4 h-4 mr-1" />
                          {isRTL ? 'تحميل' : 'Download'}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* New Request Dialog */}
      <Dialog open={showNewRequest} onOpenChange={setShowNewRequest}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{isRTL ? 'طلب خطاب جديد' : 'Request New Letter'}</DialogTitle>
            <DialogDescription>
              {isRTL 
                ? 'اختر نوع الخطاب واللغة المطلوبة'
                : 'Select the letter type and language preference'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{isRTL ? 'نوع الخطاب' : 'Letter Type'}</Label>
              <Select
                value={newRequest.letter_type_code}
                onValueChange={(value) => setNewRequest({ ...newRequest, letter_type_code: value })}
              >
                <SelectTrigger data-testid="letter-type-select">
                  <SelectValue placeholder={isRTL ? 'اختر نوع الخطاب' : 'Select letter type'} />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  {letterTypes.map((type) => (
                    <SelectItem key={type.code} value={type.code}>
                      {isRTL ? type.name_ar : type.name_en}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>{isRTL ? 'اللغة' : 'Language'}</Label>
              <Select
                value={newRequest.language}
                onValueChange={(value) => setNewRequest({ ...newRequest, language: value })}
              >
                <SelectTrigger data-testid="language-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LANGUAGE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {isRTL ? opt.labelAr : opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>{isRTL ? 'ملاحظات (اختياري)' : 'Notes (Optional)'}</Label>
              <Textarea
                value={newRequest.employee_notes}
                onChange={(e) => setNewRequest({ ...newRequest, employee_notes: e.target.value })}
                placeholder={isRTL ? 'أي تفاصيل إضافية...' : 'Any additional details...'}
                rows={3}
                data-testid="employee-notes-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewRequest(false)}>
              {isRTL ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button onClick={handleSubmitRequest} disabled={submitting} data-testid="submit-request-btn">
              {submitting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              {isRTL ? 'تقديم الطلب' : 'Submit Request'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Request Details Dialog */}
      <Dialog open={!!selectedRequest} onOpenChange={() => setSelectedRequest(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{isRTL ? 'تفاصيل الطلب' : 'Request Details'}</DialogTitle>
          </DialogHeader>
          
          {selectedRequest && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">{isRTL ? 'نوع الخطاب' : 'Letter Type'}</p>
                  <p className="font-medium">
                    {isRTL ? selectedRequest.letter_type_name_ar : selectedRequest.letter_type_name_en}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">{isRTL ? 'الحالة' : 'Status'}</p>
                  <Badge className={STATUS_CONFIG[selectedRequest.status]?.color}>
                    {isRTL 
                      ? STATUS_CONFIG[selectedRequest.status]?.labelAr 
                      : STATUS_CONFIG[selectedRequest.status]?.label}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-gray-500">{isRTL ? 'تاريخ الطلب' : 'Request Date'}</p>
                  <p className="font-medium">{formatDate(selectedRequest.created_at)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">{isRTL ? 'اللغة' : 'Language'}</p>
                  <p className="font-medium">
                    {LANGUAGE_OPTIONS.find(o => o.value === selectedRequest.language)?.[isRTL ? 'labelAr' : 'label']}
                  </p>
                </div>
              </div>

              {selectedRequest.employee_notes && (
                <div>
                  <p className="text-sm text-gray-500">{isRTL ? 'ملاحظاتي' : 'My Notes'}</p>
                  <p className="text-gray-700 bg-gray-50 p-3 rounded-lg mt-1">
                    {selectedRequest.employee_notes}
                  </p>
                </div>
              )}

              {selectedRequest.rejection_reason && (
                <div className="border-l-4 border-red-500 bg-red-50 p-4 rounded">
                  <p className="text-sm font-medium text-red-800">
                    {isRTL ? 'سبب الرفض' : 'Rejection Reason'}
                  </p>
                  <p className="text-red-700 mt-1">{selectedRequest.rejection_reason}</p>
                </div>
              )}

              {selectedRequest.status === 'issued' && selectedRequest.issued_document_id && (
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => {
                      setPreviewDialog({ 
                        open: true, 
                        document: { 
                          id: selectedRequest.issued_document_id, 
                          file_name: `Letter-${selectedRequest.id.slice(0,8)}.pdf`
                        }
                      });
                      setSelectedRequest(null);
                    }}
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    {isRTL ? 'معاينة الخطاب' : 'Preview Letter'}
                  </Button>
                  <Button
                    className="flex-1"
                    onClick={() => {
                      handleDownload(selectedRequest.issued_document_id, `Letter-${selectedRequest.id.slice(0,8)}`);
                      setSelectedRequest(null);
                    }}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    {isRTL ? 'تحميل الخطاب' : 'Download Letter'}
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Phase 25.1: Document Preview for Letters */}
      <DocumentPreview
        open={previewDialog.open}
        onClose={() => setPreviewDialog({ open: false, document: null })}
        document={previewDialog.document ? {
          id: previewDialog.document.id,
          file_name: previewDialog.document.file_name || 'letter.pdf',
          mime_type: 'application/pdf'
        } : null}
        downloadUrl={previewDialog.document ? `${API}/api/letters/my/documents/${previewDialog.document.id}/download` : null}
        token={token}
        isRTL={isRTL}
        documentType="letter"
      />
    </div>
  );
}
